import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { DjayAvatar } from '../../mascot';
import type { DjayEmotion, DjayVariant } from '../../mascot';
import { MissionBriefing } from '../MissionBriefing';
interface LabHeroProps {
  labWelcome: {
    text: string;
    emotion: DjayEmotion;
  };
  selectedVariant: DjayVariant;
  allModesLength: number;
  onModeClick: (modeId: string, familyId?: string) => void;
}
/**
 * LabHero — Compact welcome surface for the Learning Lab.
 *
 * Combines Djay's contextual greeting with the MissionBriefing recommendation
 * cards. Extracted from LearningLabPage during the Wave C structural refactor
 * (F-CODE-3) — the page itself is now a thin orchestrator.
 *
 * Part of the learningLab domain (DDD co-locatie s57).
 */
export function LabHero({
  labWelcome,
  selectedVariant,
  allModesLength,
  onModeClick
}: LabHeroProps) {
  return <section aria-labelledby="lab-hero-heading" className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8 space-y-6" data-id="element-2755">
      {/* Header row: Djay greeting + lab stat */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-5" data-id="element-2756">
        <div className="flex items-start gap-4 flex-1 min-w-0" data-id="element-2757">
          <div className="flex-shrink-0" data-id="element-2758">
            <DjayAvatar size="lg" emotion={labWelcome.emotion} variant={selectedVariant} animate={true} data-id="element-2759" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-2760">
            <div className="flex items-center gap-2 mb-1.5" data-id="element-2761">
              <Sparkles className="w-4 h-4 text-fuchsia-500" strokeWidth={2.5} data-id="element-2762" />
              <span className="text-[11px] font-black uppercase tracking-wider text-fuchsia-600" data-id="element-2763">
                Welkom in het Oefenlab
              </span>
            </div>
            <h2 id="lab-hero-heading" className="text-xl sm:text-2xl font-black text-slate-900 leading-tight" data-id="element-2764">
              {labWelcome.text}
            </h2>
          </div>
        </div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        delay: 0.1
      }} className="flex-shrink-0 self-start sm:self-center rounded-2xl bg-fuchsia-50 border border-fuchsia-100 px-4 py-3 text-center" aria-label={`${allModesLength} oefenmodi beschikbaar`} data-id="element-2765">
          <div className="text-2xl font-black text-fuchsia-700 leading-none" data-id="element-2766">
            {allModesLength}
          </div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-fuchsia-600 mt-1" data-id="element-2767">
            manieren
          </div>
        </motion.div>
      </div>

      {/* Mission Briefing — Djay's two recommendations */}
      <div className="pt-2" data-id="element-2768">
        <MissionBriefing onModeClick={onModeClick} data-id="element-2769" />
      </div>
    </section>;
}